import axios from 'axios';
import toast from 'react-hot-toast';

// Create axios instance
// Use a relative /api by default so the CRA devServer proxy (package.json -> proxy)
// can forward requests to the backend. Falling back to an env variable lets
// production override the full backend URL when needed.
// Default to direct backend URL in development to avoid proxy pitfalls.
// You can override this by setting REACT_APP_API_URL in your environment.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const { response, config } = error;
    const originalRequest = config;

    // Network error (no response) — backend likely down or proxy misconfigured
    if (!response) {
      toast.error('Cannot reach backend server. Please ensure the API server is running.');
      return Promise.reject(error);
    }

    // Handle token refresh
    if (response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the token
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const res = await authAPI.refreshToken({ refreshToken });
        if (res.data?.token) {
          localStorage.setItem('token', res.data.token);
          // Update the failed request's authorization header
          originalRequest.headers.Authorization = `Bearer ${res.data.token}`;
          // Retry the original request
          return api(originalRequest);
        }
      } catch (refreshError) {
        // If refresh failed, clear auth and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
        return Promise.reject(refreshError);
      }
    }
    
    if (response?.status === 403) {
      toast.error('Access denied. You do not have permission to perform this action.');
    }
    
    if (response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    }
    
    if (response?.status === 404) {
      toast.error('Resource not found.');
    }
    
    if (response?.status === 400) {
      const message = response.data?.message || 'Bad request';
      toast.error(message);
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh'),
  sendOTP: (data) => api.post('/auth/send-otp', data),
  verifyOTP: (data) => api.post('/auth/verify-otp', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// User API
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  getAddresses: () => api.get('/users/addresses'),
  addAddress: (data) => api.post('/users/addresses', data),
  updateAddress: (id, data) => api.put(`/users/addresses/${id}`, data),
  deleteAddress: (id) => api.delete(`/users/addresses/${id}`),
  getPaymentMethods: () => api.get('/users/payment-methods'),
  addPaymentMethod: (data) => api.post('/users/payment-methods', data),
  deletePaymentMethod: (id) => api.delete(`/users/payment-methods/${id}`),
  getWishlist: () => api.get('/users/wishlist'),
  addToWishlist: (productId) => api.post('/users/wishlist', { productId }),
  removeFromWishlist: (productId) => api.delete(`/users/wishlist/${productId}`),
  getNotifications: () => api.get('/users/notifications'),
  markNotificationAsRead: (id) => api.put(`/users/notifications/${id}/read`),
  getEnergyData: () => api.get('/users/energy-data'),
};

// Product API
export const productAPI = {
  getProducts: (params) => api.get('/products', { params }),
  getProductById: (id) => api.get(`/products/${id}`),
  getFeaturedProducts: () => api.get('/products/featured'),
  getCategories: () => api.get('/products/categories'),
  searchProducts: (params) => api.get('/products/search', { params }),
  addReview: (productId, review) => api.post(`/products/${productId}/reviews`, review),
  getReviews: (productId, params) => api.get(`/products/${productId}/reviews`, { params }),
  reportProduct: (productId, reason) => api.post(`/products/${productId}/report`, { reason }),
};

// Cart API
export const cartAPI = {
  getCart: () => api.get('/cart'),
  addItem: (data) => api.post('/cart/items', data),
  updateItem: (data) => api.put('/cart/items', data),
  removeItem: (productId) => api.delete(`/cart/items/${productId}`),
  clearCart: () => api.delete('/cart'),
  applyCoupon: (code) => api.post('/cart/coupon', { code }),
  removeCoupon: () => api.delete('/cart/coupon'),
};

// Order API
export const orderAPI = {
  createOrder: (data) => api.post('/orders', data),
  getOrders: (params) => api.get('/orders', { params }),
  getOrderById: (id) => api.get(`/orders/${id}`),
  updateOrderStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
  cancelOrder: (id, reason) => api.put(`/orders/${id}/cancel`, { reason }),
  requestReturn: (id, data) => api.post(`/orders/${id}/return`, data),
  trackOrder: (id) => api.get(`/orders/${id}/track`),
  addOrderNote: (id, note) => api.post(`/orders/${id}/notes`, { note }),
};

// Payment API
export const paymentAPI = {
  createPayment: (data) => api.post('/payments', data),
  verifyPayment: (id) => api.get(`/payments/${id}/verify`),
  getPaymentHistory: (params) => api.get('/payments', { params }),
  initiateMpesaPayment: (data) => api.post('/payments/mpesa', data),
  verifyMpesaPayment: (checkoutRequestId) => api.get(`/payments/mpesa/${checkoutRequestId}`),
  processRefund: (paymentId, amount) => api.post(`/payments/${paymentId}/refund`, { amount }),
};

// Partner API
export const partnerAPI = {
  getDashboard: () => api.get('/partners/dashboard'),
  getProfile: () => api.get('/partners/profile'),
  updateProfile: (data) => api.put('/partners/profile', data),
  getProducts: (params) => api.get('/partners/products', { params }),
  createProduct: (data) => api.post('/partners/products', data),
  updateProduct: (id, data) => api.put(`/partners/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/partners/products/${id}`),
  getOrders: (params) => api.get('/partners/orders', { params }),
  acceptOrder: (id) => api.put(`/partners/orders/${id}/accept`),
  rejectOrder: (id, reason) => api.put(`/partners/orders/${id}/reject`, { reason }),
  markAsShipped: (id, trackingInfo) => api.put(`/partners/orders/${id}/ship`, trackingInfo),
  getInventory: (params) => api.get('/partners/inventory', { params }),
  updateInventory: (data) => api.put('/partners/inventory', data),
  getFinanceData: (params) => api.get('/partners/finance', { params }),
  requestPayout: (amount) => api.post('/partners/payouts', { amount }),
  getPayouts: (params) => api.get('/partners/payouts', { params }),
  getReports: (params) => api.get('/partners/reports', { params }),
  uploadDocument: (data) => api.post('/partners/documents', data),
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  
  // Product Management
  getPendingProducts: (params) => api.get('/admin/products/pending', { params }),
  approveProduct: (id, data) => api.put(`/admin/products/${id}/approve`, data),
  rejectProduct: (id, reason) => api.put(`/admin/products/${id}/reject`, { reason }),
  getAllProducts: (params) => api.get('/admin/products', { params }),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  
  // Partner Management
  getPartners: (params) => api.get('/admin/partners', { params }),
  approvePartner: (id) => api.put(`/admin/partners/${id}/approve`),
  suspendPartner: (id, reason) => api.put(`/admin/partners/${id}/suspend`, { reason }),
  updatePartnerCommission: (id, data) => api.put(`/admin/partners/${id}/commission`, data),
  
  // Order Management
  getAllOrders: (params) => api.get('/admin/orders', { params }),
  updateOrderStatus: (id, status) => api.put(`/admin/orders/${id}/status`, { status }),
  assignTechnician: (orderId, technicianId) => api.put(`/admin/orders/${orderId}/assign`, { technicianId }),
  
  // Finance Management
  getCommissionSettings: () => api.get('/admin/finance/commissions'),
  updateCommissionSettings: (data) => api.put('/admin/finance/commissions', data),
  processPayouts: (data) => api.post('/admin/finance/payouts', data),
  getPayoutHistory: (params) => api.get('/admin/finance/payouts', { params }),
  
  // User Management
  getUsers: (params) => api.get('/admin/users', { params }),
  suspendUser: (id, reason) => api.put(`/admin/users/${id}/suspend`, { reason }),
  
  // Analytics
  getAnalytics: (params) => api.get('/admin/analytics', { params }),
  getReports: (params) => api.get('/admin/reports', { params }),
  
  // System Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
};

// Technician API
export const technicianAPI = {
  getDashboard: () => api.get('/technicians/dashboard'),
  getJobs: (params) => api.get('/technicians/jobs', { params }),
  getJobById: (id) => api.get(`/technicians/jobs/${id}`),
  acceptJob: (id) => api.put(`/technicians/jobs/${id}/accept`),
  startJob: (id) => api.put(`/technicians/jobs/${id}/start`),
  completeJob: (id, data) => api.put(`/technicians/jobs/${id}/complete`, data),
  updateJobStatus: (id, status, notes) => api.put(`/technicians/jobs/${id}/status`, { status, notes }),
  uploadJobPhoto: (id, photo) => api.post(`/technicians/jobs/${id}/photos`, photo),
  getServiceHistory: (params) => api.get('/technicians/service-history', { params }),
  updateProfile: (data) => api.put('/technicians/profile', data),
};

// IoT Device API
export const iotAPI = {
  getDevices: (params) => api.get('/iot/devices', { params }),
  getDeviceById: (id) => api.get(`/iot/devices/${id}`),
  activateDevice: (serialNumber, data) => api.post(`/iot/devices/${serialNumber}/activate`, data),
  getDeviceTelemetry: (id, params) => api.get(`/iot/devices/${id}/telemetry`, { params }),
  updateDeviceSettings: (id, settings) => api.put(`/iot/devices/${id}/settings`, settings),
  sendDeviceCommand: (id, command) => api.post(`/iot/devices/${id}/command`, command),
  getDeviceAlerts: (id, params) => api.get(`/iot/devices/${id}/alerts`, { params }),
};

// Upload API
export const uploadAPI = {
  uploadFile: (file, folder = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    
    return api.post('/uploads', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadMultipleFiles: (files, folder = 'general') => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('folder', folder);
    
    return api.post('/uploads/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteFile: (fileUrl) => api.delete('/uploads', { data: { fileUrl } }),
};

// Notification API
export const notificationAPI = {
  sendPushNotification: (data) => api.post('/notifications/push', data),
  sendSMS: (data) => api.post('/notifications/sms', data),
  sendEmail: (data) => api.post('/notifications/email', data),
  getNotificationTemplates: () => api.get('/notifications/templates'),
  createNotificationTemplate: (data) => api.post('/notifications/templates', data),
};

// Analytics API
export const analyticsAPI = {
  trackEvent: (event, data) => api.post('/analytics/events', { event, data }),
  getUserAnalytics: (params) => api.get('/analytics/users', { params }),
  getProductAnalytics: (params) => api.get('/analytics/products', { params }),
  getSalesAnalytics: (params) => api.get('/analytics/sales', { params }),
  getTrafficAnalytics: (params) => api.get('/analytics/traffic', { params }),
};

export default api;